<script setup lang="ts">
withDefaults(
  defineProps<{
    // Couleur du fondu des bords, à accorder au fond de la section hôte.
    fadeClass?: string;
    gapClass?: string;
  }>(),
  {
    fadeClass: "from-white via-white",
    gapClass: "gap-2",
  }
);

const { t } = useI18n();

const scrollRef = useTemplateRef<HTMLElement>("scroll");
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

function updateScrollState() {
  const el = scrollRef.value;
  if (!el) {
    canScrollLeft.value = false;
    canScrollRight.value = false;
    return;
  }
  canScrollLeft.value = el.scrollLeft > 1;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
}

// Défile d'une « page » (largeur visible) dans `direction` (1 = droite, -1 = gauche),
// sans jamais dépasser ce qu'il reste à défiler de ce côté — autrement la WebView
// peut sur-défiler élastiquement et donner l'impression d'un saut d'un écran entier
// alors qu'il ne reste qu'une vignette à révéler.
function scrollByPage(direction: number) {
  const el = scrollRef.value;
  if (!el) return;
  const remaining = Math.max(0, direction > 0 ? el.scrollWidth - el.clientWidth - el.scrollLeft : el.scrollLeft);
  const distance = Math.min(el.clientWidth, remaining);
  el.scrollBy({ left: distance * direction, behavior: "smooth" });
}

let resizeObserver: ResizeObserver | undefined;
let mutationObserver: MutationObserver | undefined;

onMounted(() => {
  const el = scrollRef.value;
  if (!el) return;
  updateScrollState();
  resizeObserver = new ResizeObserver(updateScrollState);
  resizeObserver.observe(el);
  mutationObserver = new MutationObserver(updateScrollState);
  mutationObserver.observe(el, { childList: true, subtree: true });
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
});
</script>

<template>
  <div class="relative min-w-0">
    <div ref="scroll" :class="['flex items-center overflow-x-auto scrollbar-hide whitespace-nowrap p-1 -m-1', gapClass]" @scroll.passive="updateScrollState">
      <slot />
    </div>

    <div
      v-show="canScrollLeft"
      :class="['absolute -inset-y-1 -left-4 pl-1 flex items-center pr-20 bg-linear-to-r via-25% to-transparent pointer-events-none', fadeClass]"
    >
      <UButton
        square
        variant="ghost"
        color="primary"
        icon="icon:chevronLeft"
        :aria-label="t('ui.scroll.left')"
        class="pointer-events-auto p-2.5"
        @click="scrollByPage(-1)"
      />
    </div>

    <div
      v-show="canScrollRight"
      :class="['absolute -inset-y-1 -right-4 pr-1 flex items-center pl-20 bg-linear-to-l via-25% to-transparent pointer-events-none', fadeClass]"
    >
      <UButton
        square
        variant="ghost"
        color="primary"
        icon="icon:chevronRight"
        :aria-label="t('ui.scroll.right')"
        class="pointer-events-auto p-2.5"
        @click="scrollByPage(1)"
      />
    </div>
  </div>
</template>
